import { useEffect, useRef, useState } from 'react';
import type { MqttConnectionState } from '@bms/common';

/** How long a first connection may take before the desk is told it is not live. */
export const FIRST_CONNECT_GRACE_MS = 10_000;

/**
 * When the live session went down, or `null` while it is up (audit UX-24). A
 * session that was live and dropped counts from the drop; one that has never
 * connected counts from page load once a short grace has passed, so an
 * ordinary first connect never flashes the strip.
 */
export function useLivePausedSince(state: MqttConnectionState): Date | null {
  const hadConnected = useRef(false);
  const mountedAt = useRef(new Date());
  const [since, setSince] = useState<Date | null>(null);

  // Connected clears the moment while rendering; a drop is stamped by a timer —
  // at once after a live session, after the grace before the first connect.
  if (state === 'connected' && since !== null) setSince(null);
  useEffect(() => {
    if (state === 'connected') {
      hadConnected.current = true;
      return;
    }
    const dropped = hadConnected.current;
    const wait = dropped ? 0 : FIRST_CONNECT_GRACE_MS - (Date.now() - mountedAt.current.getTime());
    const timer = window.setTimeout(() => setSince((previous) => previous ?? (dropped ? new Date() : mountedAt.current)), Math.max(0, wait));
    return () => window.clearTimeout(timer);
  }, [state]);

  return since;
}
